import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * HomeHeader Component - FIXED to match Laravel home_header.blade.php exactly
 * 
 * Key fixes from original React version:
 * 1. Uses fixed-top navbar class like Laravel
 * 2. Matches exact 3-column desktop layout (col-4, col-4, col-4)
 * 3. Uses custom classes: custom-xl-3, custom-col-29, cus-lg-col
 * 4. Logo + brown-line + app icons structure preserved
 * 5. Uses .hideable-tag class for "Get Our Apps"
 * 6. Uses .nav-text class for navigation links
 * 7. Uses .video-img CSS class for video icon
 * 8. Proper mobile collapse menu structure
 * 9. btn-lg classes on desktop buttons
 * 10. Bug #4 fix: Sidebar can now close properly
 */
const HomeHeader = () => {
  const { isAuthenticated, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const getDashboardLink = () => {
    if (!user) return '/login';
    
    // Role ID mapping (matches Laravel convention)
    // 1 = Super Admin, 2 = Admin, 3 = Participant, 4 = Individual
    const roleRoutes = {
      1: '/admin-dashboard', // Super Admin uses admin dashboard
      2: '/admin-dashboard',
      3: '/participant-dashboard',
      4: '/individual-dashboard',
    };
    
    // Try activeRoleId first, then check if it's a valid number
    const roleId = user.activeRoleId;
    
    if (roleId && roleRoutes[roleId]) {
      return roleRoutes[roleId];
    }
    
    // Fallback: if user is logged in but role is unknown, go to admin dashboard
    console.warn('Unknown role ID:', roleId, 'User:', user);
    return '/admin-dashboard';
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className="header">
      <nav className="navbar navbar-expand-lg fixed-top home-header" ref={navRef}>
        <div className="container-fluid d-lg-block">
          {/* Mobile Toggler - Bug #4 fix: Added onClick handler */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Mobile Logo */}
          <img
            src="/images/logo_image.webp"
            alt="FMW-logo"
            className="home-logo py-2 d-lg-none"
          />

          {/* Tablet Sign Buttons (visible sm to lg) */}
          <div className="d-none d-sm-block d-lg-none">
            <div className="sign-button btn-group pt-2 pt-sm-0 pe-3 mb-lg-0">
              {isAuthenticated ? (
                <Link to={getDashboardLink()} className="btn clr-blue text-nowrap text-white">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn clr-blue text-nowrap text-white">Sign Up</Link>
                  <Link to="/choose-role" className="btn clr-brown text-nowrap text-white">Sign In</Link>
                </>
              )}
            </div>
          </div>

          {/* Desktop Layout - 3 Column */}
          <div className="d-none d-lg-block mx-3">
            <div className="row">
              {/* Column 1: Logo + Brown Line + App Icons */}
              <div className="col-4 col-xxl-3 custom-xl-3 d-flex align-items-center custom-col-29">
                <img
                  src="/images/logo_image.webp"
                  alt="FMW-logo"
                  className="home-logo py-2"
                />
                <div className="brown-line mx-3 mx-lg-2 mx-xl-3"></div>
                <div className="pt-1">
                  <p className="mb-1 nav-p">
                    <a href="https://apps.apple.com/app/fundmewiz" target="_blank" rel="noopener noreferrer">
                      <span className="apple-img"></span>
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.fundmewiz" target="_blank" rel="noopener noreferrer">
                      <span className="android-img"></span>
                    </a>
                  </p>
                  <small className="hideable-tag nav-small pt-1">Get Our Apps</small>
                </div>
              </div>

              {/* Column 2: Navigation Links */}
              <div className="col-4 col-xxl-5 cus-lg-col d-flex align-items-center justify-content-xxl-evenly custom-col-29">
                <div className="d-xxl-flex align-items-center justify-content-center gap-4 gap-lg-1 link-group">
                  <p className="mb-0 nav-text mx-2">
                    <Link to="/about-admin">What is a Campaign Manager/Admin</Link>
                  </p>
                  <p className="mb-0 nav-text mx-2">
                    <Link to="/about-participant">What is an Influencer/Participant</Link>
                  </p>
                </div>
              </div>

              {/* Column 3: Why Fundraise + Buttons - Bug #3 fix: Added flex-nowrap */}
              <div className="col-4 d-lg-flex flex-nowrap ms-auto justify-content-end align-items-center pt-4 ps-4 pt-lg-0 ps-lg-0">
                <div className="mb-3 me-2 mb-lg-0">
                  <p className="mb-0 nav-p text-nowrap">
                    <a 
                      href="#why-fundraise-with-fms"
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById('why-fundraise-with-fms');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="nav-link"
                    >
                      WHY FUNDRAISE <span className="video-img"></span>
                    </a>
                  </p>
                  <small className="hideable-tag-with nav-small text-nowrap">
                    With FundMeWiz.Com
                  </small>
                </div>

                <div className="me-1 me-xl-2 mb-3 mb-lg-0 flex-shrink-0">
                  <Link to="/start-campaign" className="btn btn-brown px-2 px-xl-3 camp-btn-fs text-nowrap">
                    <i className="fa-solid fa-pen-to-square me-1"></i> Start a Campaign
                  </Link>
                </div>

                <div className="sign-button btn-group mb-3 mb-lg-0 flex-shrink-0">
                  {isAuthenticated ? (
                    <Link to={getDashboardLink()} className="btn btn-lg clr-blue text-nowrap text-white">
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link to="/register" className="btn btn-lg clr-blue text-nowrap text-white">Sign Up</Link>
                      <Link to="/choose-role" className="btn btn-lg clr-brown text-nowrap text-white">Sign In</Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Menu - Bug #4 fix: Using state-based show/hide */}
          <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="collapsibleNavbar">
            <div className="d-lg-none pt-4 ps-4 pt-lg-0 ps-lg-0">
              {/* Close Button for Mobile Menu */}

              {/* Get Our Apps */}
              <div className="mb-3">
                <p className="mb-0 nav-p">
                  <a href="https://apps.apple.com/app/fundmewiz" target="_blank" rel="noopener noreferrer">
                    <span className="apple-img"></span>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.fundmewiz" target="_blank" rel="noopener noreferrer">
                    <span className="android-img"></span>
                  </a>
                </p>
                <small className="nav-small">Get Our Apps</small>
              </div>

              {/* Why Fundraise */}
              <div className="mb-3 mb-lg-0">
                <p className="mb-0 nav-p text-nowrap">
                  <a href="#why-fundraise-with-fmw" onClick={closeMenu}>
                    WHY FUNDRAISE <span className="video-img"></span>
                  </a>
                </p>
                <small className="nav-small">With FundMeWiz.Com</small>
              </div>

              {/* Navigation Links */}
              <div className="col-12 d-flex align-items-center justify-content-lg-evenly justify-content-xxl-evenly mb-3">
                <div className="d-flex align-items-center justify-content-center gap-4 gap-lg-3 link-group">
                  <p className="mb-0 nav-text me-2">
                    <Link to="/about-admin" onClick={closeMenu}>What is a Campaign Manager/Admin</Link>
                  </p>
                  <p className="mb-0 nav-text mx-2">
                    <Link to="/about-participant" onClick={closeMenu}>What is an Influencer/Participant</Link>
                  </p>
                </div>
              </div>

              {/* Start Campaign Button */}
              <div className="me-3 mb-3 mb-lg-0">
                <Link to="/start-campaign" className="btn btn-brown text-nowrap" onClick={closeMenu}>
                  <i className="fa-solid fa-pen-to-square me-1"></i> Start a Campaign
                </Link>
              </div>

              {/* Dashboard Button */}
              {/* <div className="me-3 mb-3 mb-lg-0">
                <Link to={getDashboardLink()} className="btn btn-blue text-nowrap text-white" onClick={closeMenu}>
                  Dashboard
                </Link>
              </div> */}

              {/* Mobile Sign Buttons (visible only on xs) */}
              <div className="d-sm-none">
                <div className="sign-button btn-group mb-3 mb-lg-0">
                  {isAuthenticated ? (
                    <Link to={getDashboardLink()} className="btn clr-blue text-nowrap text-white" onClick={closeMenu}>
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link to="/register" className="btn clr-blue text-nowrap text-white" onClick={closeMenu}>Sign Up</Link>
                      <Link to="/choose-role" className="btn clr-brown text-nowrap text-white" onClick={closeMenu}>Sign In</Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default HomeHeader;